import { FormPanel } from '@checkit/react-components';
import { CancelIcon } from '@checkit/react-components/build/components/Icons';
import Button from '@material-ui/core/Button';
import { Theme } from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Toolbar from '@material-ui/core/Toolbar';
import clsx from 'clsx';
import React, { ChangeEvent, createRef, FormEvent, ReactNode, RefObject, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';

import useWindowUnload from '../../checkpoints/components/form/hooks/usePreventWindowUnload';
import { SaveIcon } from '../../common/SaveIcon';
import MaterialUiLink from '../../components/MaterialUiLink';
import RouteLeavingGuard from '../../components/routeLeaving/routeLeaving';
import { useAxiosDispatch } from '../../middleware/axios';
import { State } from '../../state';
import { appMessages } from '../../translations';
import { getCheckpointType } from '../actions';
import { CheckpointType, CheckpointTypeData } from '../state';

import { messages } from './translations';

interface OwnProps {
  code: string | null;
  onSubmit: (data: CheckpointTypeData) => Promise<AnyAction>;
  children?: ReactNode;
}

export interface Props extends OwnProps {
  checkpointType: CheckpointType | null;
}

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    margin: theme.spacing(1)
  },
  formInput: {
    marginTop: theme.spacing(0)
  },
  iconSmall: {
    fontSize: 20
  },
  leftIcon: {
    marginRight: theme.spacing(1)
  },
  toolBar: {
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'center'
    },
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3)
  }
}));

const MAX_NAME_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 100;

export const CheckpointTypeFormImpl = (props: Props) => {
  const { checkpointType, code } = props;
  const { formatMessage } = useIntl();
  const dispatch = useAxiosDispatch( );
  const classes = useStyles();
  const formRef = createRef() as RefObject<ValidatorForm>;

  const [dirty, setDirty] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  useWindowUnload(dirty);

  useEffect(() => {
    if (code) {
      dispatch(getCheckpointType(code));
    }
  }, [code, dispatch]);

  useEffect(() => {
    if (checkpointType) {
      setName(checkpointType.name);
      setDescription(checkpointType.description);
    }
  }, [checkpointType]);

  const onFormChange = (_: ChangeEvent<HTMLInputElement>) => {
    setDirty(true);
  };

  const onChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const onChangeDescription = (event: ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  const onSubmit = async (event: FormEvent) => {
    let isFormValid = false;

    event.preventDefault();

    // Form validation
    if (formRef.current) {
      isFormValid = await formRef.current.isFormValid(false);
    }

    if (isFormValid) {
      setFormSubmitting(true);
      setDirty(false);

      const resultingAction = await props.onSubmit({ name, description });

      // If the action was successful, the component won't be mounted and we get a warning
      if (resultingAction.type.indexOf('_SUCCESS') === -1) {
        setFormSubmitting(false);
      }
    }
  };

  return (
    <>
      <ValidatorForm ref={formRef} onSubmit={onSubmit} onChange={onFormChange}>
        <FormPanel
          heading={formatMessage(messages['checkpointType.form.heading'])}
          name="checkpoint-type"
          initialExpanded={true}
        >
          <TextValidator
            fullWidth={true}
            id="name"
            className={classes.formInput}
            label={formatMessage(messages['checkpointType.form.name'])}
            name="name"
            autoFocus={true}
            value={name}
            withRequiredValidator={true}
            validators={['required', 'trim', `maxStringLength:${MAX_NAME_LENGTH}`]}
            errorMessages={[
              formatMessage(
                appMessages['app.required'],
                { fieldName: formatMessage(messages['checkpointType.form.name']) }
              ),
              formatMessage(
                appMessages['app.nonBlank'],
                { fieldName: formatMessage(messages['checkpointType.form.name']) }
              ),
              formatMessage(
                appMessages['app.maxLength'],
                {
                  fieldName: formatMessage(messages['checkpointType.form.name']),
                  maxLength: MAX_NAME_LENGTH
                }
              )
            ]}
            onChange={onChangeName}
          />
          <TextValidator
            fullWidth={true}
            id="description"
            className={classes.formInput}
            label={formatMessage(messages['checkpointType.form.description'])}
            name="description"
            value={description}
            validators={[`maxStringLength:${MAX_DESCRIPTION_LENGTH}`]}
            errorMessages={[
              formatMessage(
                appMessages['app.maxLength'],
                {
                  fieldName: formatMessage(messages['checkpointType.form.description']),
                  maxLength: MAX_DESCRIPTION_LENGTH
                }
              )
            ]}
            onChange={onChangeDescription}
          />
        </FormPanel>
        <Toolbar className={classes.toolBar}>
          <Button
            variant="contained"
            color="secondary"
            className={classes.button}
            component={MaterialUiLink}
            to="/checkpointtypes"
          >
            <CancelIcon className={clsx(classes.leftIcon, classes.iconSmall)} />
            {formatMessage(appMessages['app.cancel'])}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={classes.button}
            disabled={formSubmitting}
          >
            <SaveIcon formSubmitting={formSubmitting} />
            {formatMessage(appMessages['app.save'])}
          </Button>
        </Toolbar>
      </ValidatorForm>
      <RouteLeavingGuard when={dirty}/>
    </>
  );
};

const mapStateToProps = (state: State, props: OwnProps) => ({
  checkpointType: props.code ? state.checkpointTypes.byCode[props.code] || null : null
});
export default connect(mapStateToProps)(CheckpointTypeFormImpl);
